import React from 'react';
import {
  Box, Typography, Stack, FormControl, InputLabel, Select, MenuItem, TextField,
  IconButton, Button, Autocomplete, Tooltip,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import useMpiWorkItemFields from '../hooks/useMpiWorkItemFields';

// Saved as the form-level `print_criteria` custom_form_property (JSON). Decides
// which mpi_work_items appear on the printed PDF; no rules = report default.
export const DEFAULT_PRINT_CRITERIA = { match_mode: 'any', rules: [] };

export const PRINT_OPERATORS = [
  { value: 'eq', label: 'equals' },
  { value: 'ne', label: 'does not equal' },
];

export const parsePrintCriteria = (value) => {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return {
      match_mode: parsed?.match_mode === 'all' ? 'all' : 'any',
      rules: Array.isArray(parsed?.rules)
        ? parsed.rules.filter(r => r && r.field).map(r => ({
          field: String(r.field),
          operator: PRINT_OPERATORS.some(o => o.value === r.operator) ? r.operator : 'eq',
          value: r.value == null ? '' : String(r.value),
        }))
        : [],
    };
  } catch (e) {
    return { ...DEFAULT_PRINT_CRITERIA };
  }
};

const PrintCriteriaConfig = ({ printCriteria, setPrintCriteria }) => {
  const fields = useMpiWorkItemFields();
  const criteria = printCriteria || DEFAULT_PRINT_CRITERIA;
  const rules = criteria.rules || [];

  const setRules = updater => setPrintCriteria(prev => ({
    ...(prev || DEFAULT_PRINT_CRITERIA),
    rules: updater((prev && prev.rules) || []),
  }));
  const updateRule = (index, patch) => setRules(list => list.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const addRule = () => setRules(list => [...list, { field: 'approved', operator: 'eq', value: 'true' }]);
  const removeRule = index => setRules(list => list.filter((_, i) => i !== index));

  return (
    <Box sx={{
 mt: 4, p: 3, bgcolor: '#f5f5f5', borderRadius: 1,
}}
    >
      <Typography variant="h6">Print Criteria</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Which work items appear on the printed PDF. With no rules, the report uses its default.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Match</InputLabel>
          <Select
            label="Match"
            value={criteria.match_mode || 'any'}
            onChange={e => setPrintCriteria(prev => ({ ...(prev || DEFAULT_PRINT_CRITERIA), match_mode: e.target.value }))}
          >
            <MenuItem value="any">Include if ANY rule matches (OR)</MenuItem>
            <MenuItem value="all">Include if ALL rules match (AND)</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Stack spacing={1}>
        {rules.length === 0 && (
          <Typography variant="body2" color="textSecondary">No rules: the report default is used.</Typography>
        )}
        {rules.map((rule, index) => (
          <Box
key={index}
sx={{
 display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap',
}}
          >
            <Autocomplete
              size="small"
              sx={{ width: 280 }}
              options={fields}
              value={rule.field || null}
              onChange={(e, value) => updateRule(index, { field: value || '' })}
              renderInput={params => <TextField {...params} label="mpi_work_item column" />}
              noOptionsText={fields.length ? 'No matching column' : 'Column list unavailable'}
            />
            <FormControl size="small" sx={{ width: 170 }}>
              <InputLabel>Operator</InputLabel>
              <Select label="Operator" value={rule.operator} onChange={e => updateRule(index, { operator: e.target.value })}>
                {PRINT_OPERATORS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Value"
              placeholder="true"
              value={rule.value}
              onChange={e => updateRule(index, { value: e.target.value })}
              sx={{ width: 170 }}
            />
            <Tooltip title="Remove rule">
              <IconButton size="small" color="error" onClick={() => removeRule(index)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Stack>
      <Button size="small" startIcon={<Add />} onClick={addRule} sx={{ mt: 1 }}>Add rule</Button>
    </Box>
  );
};

export default PrintCriteriaConfig;
